// ─────────────────────────────────────────────────────────────────
// Cliente de API — ÚNICO punto de contacto con el backend.
//
// ESTADO ACTUAL: MOCK. No hay Azure Functions conectado todavía.
// Cuando el backend esté listo:
//   1. Poné VITE_API_BASE_URL en .env (ver .env.example)
//   2. Cambiá USE_MOCKS a false
//   3. Nada más debería cambiar — todas las páginas consumen
//      las funciones de este archivo, no fetch() directo.
// ─────────────────────────────────────────────────────────────────

import type {
  Agent,
  AgentRunLog,
  AgentSchedule,
  Platform,
  Skill,
  Tool,
  JobBoardRequisition,
  PanelUser,
} from '@/types'
import { auth } from './firebase'
import { mockAgents, mockTools, mockSkills, mockPlatforms, mockSchedules, mockRunLogs, mockJobBoard, mockUsers } from './mockData'

const USE_MOCKS = true // TODO: false cuando el Function App esté desplegado

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

async function authHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser
  if (!user) return {}
  const token = await user.getIdToken()
  return { Authorization: `Bearer ${token}` }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if (!API_BASE_URL) {
    throw new Error(
      `API_BASE_URL no configurado. Definí VITE_API_BASE_URL para llamar a ${path}.`,
    )
  }
  const headers = { ...(await authHeader()), 'Content-Type': 'application/json', ...(init?.headers ?? {}) }
  const res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error ?? `Error ${res.status} en ${path}`)
  }
  return res.json()
}

function delay<T>(value: T, ms = 250): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms))
}

// ── Agents ──────────────────────────────────────────────────────

export async function listAgents(): Promise<Agent[]> {
  if (USE_MOCKS) return delay(mockAgents)
  return request<Agent[]>('/agents')
}

export async function getAgent(id: string): Promise<Agent | null> {
  if (USE_MOCKS) return delay(mockAgents.find((a) => a.id === id) ?? null)
  return request<Agent>(`/agents/${id}`)
}

export async function compileIdentity(
  id: string,
  systemPrompt: string,
): Promise<{ ok: boolean; version: number; tag: string | null }> {
  if (USE_MOCKS) return delay({ ok: true, version: 99, tag: `${id}-v99` })
  return request(`/agents/${id}/identity/compile`, {
    method: 'POST',
    body: JSON.stringify({ systemPrompt }),
  })
}

export async function runHeartbeat(id: string): Promise<{ ok: boolean; dry_run: boolean; payload: unknown }> {
  if (USE_MOCKS) return delay({ ok: true, dry_run: true, payload: { note: 'mock — sin backend conectado' } })
  return request(`/agents/${id}/heartbeat/run`, { method: 'POST' })
}

export async function spawnAgent(agentId: string): Promise<{ ok: boolean; dry_run: boolean }> {
  if (USE_MOCKS) return delay({ ok: true, dry_run: true })
  return request('/v1/spawn', { method: 'POST', body: JSON.stringify({ agent_id: agentId }) })
}

export async function listAgentSchedules(agentId: string): Promise<AgentSchedule[]> {
  if (USE_MOCKS) return delay(mockSchedules.filter((s) => s.agent_id === agentId))
  return request(`/agents/${agentId}/schedules`)
}

export async function listAgentRunLogs(agentId: string): Promise<AgentRunLog[]> {
  if (USE_MOCKS) return delay(mockRunLogs.filter((r) => r.agent_id === agentId))
  return request(`/agents/${agentId}/runs`)
}

export async function listAgentTools(agentId: string): Promise<Tool[]> {
  if (USE_MOCKS) return delay(mockTools.slice(0, 4))
  return request(`/agents/${agentId}/tools`)
}

export async function listAgentSkills(agentId: string): Promise<Skill[]> {
  if (USE_MOCKS) return delay(mockSkills.slice(0, 2))
  return request(`/agents/${agentId}/skills`)
}

// ── Catálogo ────────────────────────────────────────────────────

export async function listTools(): Promise<Tool[]> {
  if (USE_MOCKS) return delay(mockTools)
  return request('/tools')
}

export async function getTool(id: string): Promise<Tool | null> {
  if (USE_MOCKS) return delay(mockTools.find((t) => t.id === id) ?? null)
  return request(`/tools/${id}`)
}

export async function createTool(input: Tool): Promise<Tool> {
  if (USE_MOCKS) {
    mockTools.push(input)
    return delay(input)
  }
  return request('/tools', { method: 'POST', body: JSON.stringify(input) })
}

export async function updateTool(id: string, patch: Partial<Tool>): Promise<Tool> {
  if (USE_MOCKS) {
    const idx = mockTools.findIndex((t) => t.id === id)
    if (idx === -1) throw new Error('Tool no encontrada')
    mockTools[idx] = { ...mockTools[idx], ...patch }
    return delay(mockTools[idx])
  }
  return request(`/tools/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
}

export async function listSkills(): Promise<Skill[]> {
  if (USE_MOCKS) return delay(mockSkills)
  return request('/skills')
}

export async function getSkill(id: string): Promise<Skill | null> {
  if (USE_MOCKS) return delay(mockSkills.find((s) => s.id === id) ?? null)
  return request(`/skills/${id}`)
}

export async function createSkill(input: Skill): Promise<Skill> {
  if (USE_MOCKS) {
    mockSkills.push(input)
    return delay(input)
  }
  return request('/skills', { method: 'POST', body: JSON.stringify(input) })
}

export async function updateSkill(id: string, patch: Partial<Skill>): Promise<Skill> {
  if (USE_MOCKS) {
    const idx = mockSkills.findIndex((s) => s.id === id)
    if (idx === -1) throw new Error('Skill no encontrado')
    mockSkills[idx] = { ...mockSkills[idx], ...patch }
    return delay(mockSkills[idx])
  }
  return request(`/skills/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
}

export async function listPlatforms(): Promise<Platform[]> {
  if (USE_MOCKS) return delay(mockPlatforms)
  return request('/platforms')
}

// ── Job Board ───────────────────────────────────────────────────

export async function listJobBoard(): Promise<JobBoardRequisition[]> {
  if (USE_MOCKS) return delay(mockJobBoard)
  return request('/job-board')
}

export async function getJobBoardItem(id: string): Promise<JobBoardRequisition | null> {
  if (USE_MOCKS) return delay(mockJobBoard.find((j) => j.id === id) ?? null)
  return request(`/job-board/${id}`)
}

export async function createJobBoardItem(input: JobBoardRequisition): Promise<JobBoardRequisition> {
  if (USE_MOCKS) {
    mockJobBoard.push(input)
    return delay(input)
  }
  return request('/job-board', { method: 'POST', body: JSON.stringify(input) })
}

export async function updateJobBoardItem(
  id: string,
  patch: Partial<JobBoardRequisition>,
): Promise<JobBoardRequisition> {
  if (USE_MOCKS) {
    const idx = mockJobBoard.findIndex((j) => j.id === id)
    if (idx === -1) throw new Error('Requisición no encontrada')
    mockJobBoard[idx] = { ...mockJobBoard[idx], ...patch }
    return delay(mockJobBoard[idx])
  }
  return request(`/job-board/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
}

// ── Admin ───────────────────────────────────────────────────────

export async function listPanelUsers(): Promise<PanelUser[]> {
  if (USE_MOCKS) return delay(mockUsers)
  return request('/admin/users')
}

export async function inviteUser(email: string, role: string): Promise<{ ok: boolean }> {
  if (USE_MOCKS) return delay({ ok: true })
  return request('/admin/invite', { method: 'POST', body: JSON.stringify({ email, role }) })
}
